import * as Y from "yjs";
import type { z } from "zod";

/**
 * Yjs param helpers for the Hoshin canvas cards (PB-418).
 *
 * Free-text fields on the `textCard` / `linkCard` / `kpiCard` widgets are stored
 * as nested `Y.Text` inside the widget's `params` `Y.Map`, so concurrent edits
 * merge character-level instead of last-writer-wins clobbering the whole object.
 * All CRDT logic lives here + in the `YjsWidgetBody` adapter; the widget
 * Components stay data-blind (they keep the whole-object `onChange` contract).
 * Which fields are text is declared per-def via `YjsWidgetDef.textFields`;
 * everything else stays a plain LWW value.
 */

/**
 * The minimal single contiguous edit turning `before` into `after`: delete
 * `delCount` UTF-16 units at `start`, then insert `insert`. Prefix/suffix
 * boundaries are found on Unicode **code points** (via `Array.from`) so a
 * boundary never lands inside a surrogate pair — e.g. replacing `😀` with `😃`
 * (shared high surrogate) rewrites the whole code point, not a lone low
 * surrogate (PB-418 review). Offsets are then mapped back to the UTF-16 units
 * `Y.Text` indexes by.
 */
function singleRangeDiff(
  before: string,
  after: string,
): { start: number; delCount: number; insert: string } {
  const a = Array.from(before);
  const b = Array.from(after);
  const maxPrefix = Math.min(a.length, b.length);
  let p = 0;
  while (p < maxPrefix && a[p] === b[p]) p++;
  let ea = a.length;
  let eb = b.length;
  while (ea > p && eb > p && a[ea - 1] === b[eb - 1]) {
    ea--;
    eb--;
  }
  const u16 = (cps: string[], upto: number): number => {
    let n = 0;
    for (let i = 0; i < upto; i++) n += cps[i]!.length;
    return n;
  };
  const start = u16(a, p);
  return {
    start,
    delCount: u16(a, ea) - start,
    insert: b.slice(p, eb).join(""),
  };
}

/**
 * Apply an edit to a `Y.Text` as one delete + insert.
 *
 * `before` is the value the user's edit was made against (their render
 * baseline). The delta `before → after` is rebased onto the CURRENT `Y.Text`,
 * so a concurrently-received remote edit to the same field is preserved rather
 * than overwritten by a stale whole-string snapshot (PB-418 P1). When `before`
 * is omitted it defaults to the live content (plain current → after replace),
 * which is what the pure convergence/mechanics tests use. Boundaries never split
 * a surrogate pair. The caller wraps this in a transaction.
 */
export function applyStringDiff(
  text: Y.Text,
  after: string,
  before?: string,
): void {
  const base = before ?? text.toString();
  const { start, delCount, insert } = singleRangeDiff(base, after);
  if (delCount === 0 && insert === "") return;
  if (delCount > 0) text.delete(start, delCount);
  if (insert.length > 0) text.insert(start, insert);
}

function asText(value: unknown): string {
  return typeof value === "string" ? value : String(value ?? "");
}

function valueAtPath(value: unknown, path: readonly PropertyKey[]): unknown {
  let current = value;
  for (const key of path) {
    if (
      current === null ||
      (typeof current !== "object" && typeof current !== "function")
    ) {
      return undefined;
    }
    current = (current as Record<PropertyKey, unknown>)[key];
  }
  return current;
}

/**
 * Value equality for scalar params. The Component rebuilds (and Zod-clones)
 * `next` on every edit, so a bare reference compare would rewrite non-primitive
 * fields (e.g. `recentValues`) on every keystroke — emitting redundant Yjs
 * updates and LWW-clobbering a concurrent peer's write to that field even when
 * the local user only touched a different field. Structural-compare
 * non-primitives so an unchanged value is left untouched (PB-418 review).
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (
    a === null ||
    b === null ||
    typeof a !== "object" ||
    typeof b !== "object"
  ) {
    return false;
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Build a preliminary params `Y.Map` for a first materialization: each field in
 * `textFields` becomes a `Y.Text` seeded with its string value; every other
 * field is stored as a plain value (`undefined` values are skipped, matching
 * absent optionals). Returned detached — the caller integrates it via
 * `ymap.set("params", …)`, which flushes the prelim content into the doc.
 */
export function paramsToYMap(
  next: Record<string, unknown>,
  textFields: readonly string[],
): Y.Map<unknown> {
  const text = new Set(textFields);
  const pmap = new Y.Map<unknown>();
  for (const [key, value] of Object.entries(next)) {
    if (value === undefined) continue;
    pmap.set(key, text.has(key) ? new Y.Text(asText(value)) : value);
  }
  return pmap;
}

/**
 * Project a stored params value back to a plain, schema-valid object. Reads BOTH
 * shapes: the new `Y.Map` (with `Y.Text` fields projected to strings) and a
 * legacy plain object (pre-PB-418 / Storybook fixtures). Anything that fails the
 * kind's schema coerces to `defaults` — the Yjs render path does no validation
 * of its own, so this is the last line before a malformed/legacy blob reaches a
 * Component.
 */
export function paramsFromYMap<T>(
  raw: unknown,
  schema: z.ZodType<T>,
  defaults: T,
): T {
  let candidate: unknown = raw;
  if (raw instanceof Y.Map) {
    const obj: Record<string, unknown> = {};
    raw.forEach((value, key) => {
      obj[key] = value instanceof Y.Text ? value.toString() : value;
    });
    candidate = obj;
  }
  const parsed = schema.safeParse(candidate);
  if (parsed.success) return parsed.data;
  // A CRDT merge of two individually-valid near-limit edits can converge PAST
  // the editor's max-length. That's structurally fine — do NOT blank the whole
  // card (and its valid sibling fields) over a length overflow; keep the
  // projected values so the converged text stays visible and shortenable
  // (PB-418 review). Only genuinely malformed/mistyped params fall back to
  // defaults: if every Zod issue is a string length overflow (`too_big`) and the
  // candidate is object-shaped, return it as-is. Array/object size overflows
  // remain invalid; accepting those can pass attacker-sized collections to a
  // render loop even though the schema explicitly capped them.
  if (
    candidate !== null &&
    typeof candidate === "object" &&
    !Array.isArray(candidate) &&
    parsed.error.issues.length > 0 &&
    parsed.error.issues.every(
      (issue) =>
        issue.code === "too_big" &&
        typeof valueAtPath(candidate, issue.path) === "string",
    )
  ) {
    return candidate as T;
  }
  return defaults;
}

/**
 * Reconcile a whole-object `next` (from the data-blind Component) into the
 * widget `Y.Map`'s `params`, in ONE transaction, WITHOUT replacing the params
 * container — text fields get char-level `Y.Text` deltas, scalars are set (LWW)
 * or deleted. If `params` is not yet a `Y.Map` (absent, or a legacy plain
 * object), it is materialized once via `paramsToYMap`. Keeping the same `Y.Text`
 * instances across edits is what makes concurrent edits merge instead of clobber.
 *
 * `baseline` is the projection the Component last rendered from (what the user
 * saw). Only fields whose `next` value differs from `baseline` are written — a
 * whole-object `onChange` carries every field, but the ones the user did NOT
 * touch must be left alone, or reconciling them from a possibly-stale snapshot
 * reverts a concurrently-received remote edit to that sibling field (PB-418 R10).
 * `baseline` compares against what the user saw, NOT the current shared value, so
 * this holds even in the window before a remote edit has re-rendered locally.
 * Omit `baseline` (undefined) to reconcile every field — used only by the pure
 * mechanics/convergence tests, never the live adapter.
 */
export function reconcileYjsParams(
  doc: Y.Doc,
  ymap: Y.Map<unknown>,
  next: Record<string, unknown>,
  textFields: readonly string[],
  baseline?: Record<string, unknown>,
): void {
  const unchanged = (key: string, value: unknown): boolean =>
    baseline !== undefined && sameValue(baseline[key], value);
  doc.transact(() => {
    const existing = ymap.get("params");
    if (!(existing instanceof Y.Map)) {
      ymap.set("params", paramsToYMap(next, textFields));
      return;
    }
    const pmap = existing;
    const text = new Set(textFields);
    for (const [key, value] of Object.entries(next)) {
      // Skip fields the user did not change this cycle (preserves remote edits).
      if (unchanged(key, value)) continue;
      if (text.has(key)) {
        const current = pmap.get(key);
        if (current instanceof Y.Text) {
          // Rebase the user's delta (baseline → next) onto the current Y.Text,
          // not current → next — otherwise a remote same-field insert received
          // before this render committed is reverted (PB-418 P1).
          applyStringDiff(
            current,
            asText(value),
            baseline !== undefined ? asText(baseline[key]) : undefined,
          );
        } else {
          pmap.set(key, new Y.Text(asText(value)));
        }
      } else if (value === undefined) {
        if (pmap.has(key)) pmap.delete(key);
      } else if (!sameValue(pmap.get(key), value)) {
        pmap.set(key, value);
      }
    }
    // Drop keys the Component no longer sends (e.g. a cleared optional scalar).
    // With a baseline, only delete keys the user's OWN snapshot had — a key
    // present remotely but absent from both `next` and `baseline` is a remote
    // addition the local edit must not remove.
    for (const key of [...pmap.keys()]) {
      if (key in next) continue;
      if (baseline !== undefined && !(key in baseline)) continue;
      pmap.delete(key);
    }
  });
}

/** Normalize legacy containers/text identities separately from a user's edit. */
export function materializeYjsParams(
  doc: Y.Doc,
  widget: Y.Map<unknown>,
  textFields: readonly string[],
  current: Record<string, unknown>,
): void {
  doc.transact(() => {
    const params = widget.get("params");
    if (!(params instanceof Y.Map)) {
      widget.set("params", paramsToYMap(current, textFields));
      return;
    }
    for (const field of textFields) {
      const value = params.get(field);
      if (value !== undefined && !(value instanceof Y.Text))
        params.set(field, new Y.Text(asText(value)));
    }
  });
}
